import { yupResolver } from "@hookform/resolvers/yup";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import DatasetLinked from "@mui/icons-material/DatasetLinked";
import Help from "@mui/icons-material/Help";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Input,
  InputAdornment,
  Popover,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import yup from "../../../../../utils/yup";
import { isChatChannel } from "../../../../channels/types";
import { imageSizeLimit } from "../../../../lexical-chat-moment/image-size-limit";
import { INSERT_IMAGE_COMMAND } from "../../types/commands";
import { insertUrl } from "../image-plugin";
import { ToolbarIconButton } from "./toolbar-icon-button";

import type { MomentFormMoment } from "../../../../moment/types/moment-form-moment";
import type { ToolbarProps } from "./props";
import type { IconButtonProps } from "@mui/material";
import type { FC, MouseEvent } from "react";

export const URLInput: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [editor] = useLexicalComposerContext();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setFocus,
  } = useForm<{ url: string }>({
    resolver: yupResolver(
      yup.object({
        url: yup
          .string()
          .url_or_merge_tag("Enter a link in a valid format (including https://)")
          .required(),
      }),
    ),
  });

  const { control, getValues } = useFormContext<MomentFormMoment>();
  useWatch({ control, name: "channel" });
  const channel = getValues("channel");

  useEffect(() => setFocus("url"), [setFocus]);

  const onSubmit = useCallback(
    ({ url }: { url: string }) => {
      insertUrl(url, channel)
        .then((file) => editor.dispatchCommand(INSERT_IMAGE_COMMAND, file))
        .catch(console.error);

      onClose();
    },
    [channel, editor, onClose],
  );

  return (
    <Box sx={{ m: 1 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Input
          {...register("url")}
          error={Boolean(errors.url)}
          placeholder="Image URL"
          sx={{ p: 2 }}
          endAdornment={
            <InputAdornment position="end">
              <Tooltip title="When adding an image URL, please ensure that the link points directly to an image file (.jpg, .png, .gif) and that the image is accessible publicly. Inaccessible images or incorrect formats may prevent the Communication being sent.">
                <IconButton data-analytics-id="lexical-url-image-dialog-help">
                  <Help />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          }
        />

        <Button data-analytics-id="lexical-image-url-dialog-add" type="submit">
          Add
        </Button>
        {isChatChannel(channel) && (
          <Alert sx={{ mt: 1 }} severity="info">
            {`The linked image must be smaller than ${imageSizeLimit(channel)}MB`}
          </Alert>
        )}
      </form>
    </Box>
  );
};

interface ImageButtonProps extends IconButtonProps {
  label?: string;
  onClose?: () => void;
}

export const ImageButton: FC<ToolbarProps & ImageButtonProps> = ({ disabled, label, onClose }) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const togglePicker = useCallback(
    (event: MouseEvent): void => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl],
  );

  const close = useCallback(() => {
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);

  return (
    <>
      <ToolbarIconButton
        data-analytics-id="lexical-toolbar-url-image"
        disabled={disabled}
        label={label}
        icon={<DatasetLinked fontSize="medium" />}
        data-testid="toolbar-button-add-image"
        aria-label="add-image"
        tabIndex={-1}
        onClick={togglePicker}
      />
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={close}>
        <URLInput onClose={close} />
      </Popover>
    </>
  );
};
